/**
 * Ad Banner Carousel — auto-scrolling ad banners for the All tab.
 * Swipeable, auto-advances every 5s, dot indicators.
 */

import { useState, useEffect, useRef, useCallback, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAdBanners } from '@turnifly/shared/hooks'
import type { AdBannerItem } from '@turnifly/shared/api'

export const AdBannerCarousel = memo(function AdBannerCarousel(): React.ReactElement | null {
  const { data: banners } = useAdBanners()
  const navigate = useNavigate()
  const [current, setCurrent] = useState(0)
  const [direction, setDirection] = useState(0)
  const touchRef = useRef({ startX: 0, startY: 0 })
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined)

  const items = banners ?? []

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (items.length <= 1) return
    timerRef.current = setInterval(() => {
      setDirection(1)
      setCurrent((prev) => (prev + 1) % items.length)
    }, 5000)
    return () => clearInterval(timerRef.current)
  }, [items.length])

  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    touchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY }
    clearInterval(timerRef.current)
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent): void => {
    const dx = e.changedTouches[0].clientX - touchRef.current.startX
    const dy = e.changedTouches[0].clientY - touchRef.current.startY
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) {
        setDirection(1)
        setCurrent((prev) => (prev + 1) % items.length)
      } else {
        setDirection(-1)
        setCurrent((prev) => (prev - 1 + items.length) % items.length)
      }
    }
  }, [items.length])

  const handleClick = useCallback((banner: AdBannerItem): void => {
    if (banner.link_type === 'challenge') {
      navigate(`/challenges/${banner.link_target}`)
    } else if (banner.link_type === 'external') {
      window.open(banner.link_target, '_blank')
    } else if (banner.link_type === 'profile') {
      navigate(`/profile/${banner.link_target}`)
    }
  }, [navigate])

  if (items.length === 0) return null

  const banner = items[current]

  return (
    <div className="relative mb-4">
      <div
        className="relative h-[140px] rounded-2xl overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={current}
            custom={direction}
            initial={{ x: direction > 0 ? '100%' : '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: direction > 0 ? '-100%' : '100%', opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="absolute inset-0 cursor-pointer"
            onClick={() => handleClick(banner)}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${banner.image_url})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-[15px] font-display font-bold text-white leading-tight">
                {banner.title}
              </p>
              {banner.subtitle && (
                <p className="text-[12px] text-white/70 mt-0.5 line-clamp-1">
                  {banner.subtitle}
                </p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dot indicators */}
      {items.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-2.5">
          {items.map((_, idx) => (
            <button
              key={idx}
              onClick={() => { setDirection(idx > current ? 1 : -1); setCurrent(idx) }}
              className={`rounded-full transition-all tap-highlight-transparent ${
                idx === current
                  ? 'w-5 h-1.5 bg-primary'
                  : 'w-1.5 h-1.5 bg-white/20'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
})
